import { createAction, createCustomAction, deprecated } from "typesafe-actions";

import type { MainSearchSource, MainSearchSuggestionsScope } from "@/shop/api/mainSearch";
import { MainSearchActionType } from "@/shop/reducers/mainSearch/constants";
import type { IMainSearchFilterDefinition, IMainSearchFilterSetting, IMainSearchResult } from "@/shop/types/mainSearch";
import type { IPagingQuery } from "@/shop/types/searchTable";

const { createStandardAction } = deprecated;

// paging

type MainSearchSourceCollectionMeta = { collection: MainSearchSource };

type MainSearchActionData<Payload, Meta = MainSearchSourceCollectionMeta> = { payload: Payload; meta: Meta };

/**
 * Change page
 * - One-based index
 */
export const changePage = createStandardAction(MainSearchActionType.CHANGE_PAGE).map((payload: { collection: MainSearchSource; targetPage: number }) => ({
  payload
}));

export const changePageSize = createStandardAction(MainSearchActionType.CHANGE_PAGE_SIZE).map((payload: { collection: MainSearchSource; limit: number }) => ({
  payload
}));

// query

export const setSearchText = createStandardAction(MainSearchActionType.SET_SEARCH_QUERY).map((payload: string) => ({
  payload
}));

export const setSuggestionsSearchText = createStandardAction(MainSearchActionType.SET_SUGGESTIONS_QUERY).map((payload: string) => ({
  payload
}));

// result counts

export const setMainSearchResultsCount = createStandardAction(MainSearchActionType.SET_SEARCH_RESULTS_COUNT).map((payload: number) => ({
  payload
}));

export const setMainSearchSuggestionResultsCount = createStandardAction(MainSearchActionType.SET_SUGGESTIONS_RESULTS_COUNT).map((payload: number) => ({
  payload
}));

// submit

export const submitSearch = createStandardAction(MainSearchActionType.SUBMIT_SEARCH)();
export const submitSuggestionsSearch = createStandardAction(MainSearchActionType.SUBMIT_SUGGESTIONS)();

/**
 * Submits a single search tab
 */
export const submitSearchTab = createAction(
  "submit-main-search-with-filter-setting",
  (data: MainSearchActionData<null>) => data.payload,
  (data) => data.meta
)();

// reset

export const resetSearch = createStandardAction(MainSearchActionType.RESET_SEARCH_QUERY)();
export const resetSuggestionsSearch = createStandardAction(MainSearchActionType.RESET_SUGGESTIONS_QUERY)();

// fetch

export const fetchResults = createStandardAction(MainSearchActionType.FETCH_SEARCH).map(
  (payload: null, meta: { collection: MainSearchSource; query: IPagingQuery }) => ({
    payload,
    meta
  })
);

export const fetchSuggestionsResults = createStandardAction(MainSearchActionType.FETCH_SUGGESTIONS).map(
  (payload: null, meta: { collection: MainSearchSuggestionsScope }) => ({ payload, meta })
);

// fetch start

export const fetchStart = createStandardAction(MainSearchActionType.FETCH_SEARCH_START).map((payload: null, meta: { collection: MainSearchSource }) => ({
  payload,
  meta
}));

export const fetchSuggestionsStart = createStandardAction(MainSearchActionType.FETCH_SUGGESTIONS_START).map(
  (payload: null, meta: { collection: MainSearchSuggestionsScope }) => ({ payload, meta })
);

// fetch success

export const fetchSuccess = createStandardAction(MainSearchActionType.FETCH_SEARCH_SUCCESS).map(
  (payload: IMainSearchResult, meta: { collection: MainSearchSource; query: IPagingQuery }) => ({
    payload,
    meta
  })
);

export const fetchSuggestionsSuccess = createStandardAction(MainSearchActionType.FETCH_SUGGESTIONS_SUCCESS).map(
  (payload: IMainSearchResult, meta: { collection: MainSearchSuggestionsScope }) => ({
    payload,
    meta
  })
);

// fetch fail

export const fetchFail = createStandardAction(MainSearchActionType.FETCH_SEARCH_FAIL).map((payload: Error, meta: { collection: MainSearchSource }) => ({
  payload,
  meta,
  error: true
}));

export const fetchSuggestionsFail = createStandardAction(MainSearchActionType.FETCH_SUGGESTIONS_FAIL).map(
  (payload: Error, meta: { collection: MainSearchSuggestionsScope }) => ({
    payload,
    meta,
    error: true
  })
);

// clear results

export const clearResults = createStandardAction(MainSearchActionType.CLEAR_SEARCH_RESULTS).map((payload: null, meta: { collection: MainSearchSource }) => ({
  payload,
  meta
}));

export const clearSuggestionsResults = createStandardAction(MainSearchActionType.CLEAR_SUGGESTIONS_RESULTS).map(
  (payload: null, meta: { collection: MainSearchSuggestionsScope }) => ({
    payload,
    meta
  })
);

export const fetchFilterDefinitions = createAction(
  "fetch-main-search-filter-definition",
  (data: MainSearchActionData<null>) => data.payload,
  (data) => data.meta
)();

export const fetchFilterDefinitionStart = createAction(
  "fetch-main-search-filter-definition-start",
  (data: MainSearchActionData<null>) => data.payload,
  (data) => data.meta
)();

export const fetchFilterDefinitionSuccess = createAction(
  "fetch-main-search-filter-definition-success",
  (data: MainSearchActionData<IMainSearchFilterDefinition[]>) => data.payload,
  (data) => data.meta
)();

export const fetchFilterDefinitionFail = createCustomAction("fetch-main-search-filter-definition-fail", (data: MainSearchActionData<Error>) => ({
  payload: data.payload,
  meta: data.meta,
  error: true
}));

export const setFilterSetting = createAction(
  "set-main-search-filter-setting",
  (data: MainSearchActionData<IMainSearchFilterSetting[]>) => data.payload,
  (data) => data.meta
)();
